import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * THE ONE LAW for ordinary (non-grid) fields.
 *
 *            ┌──────────────── draft (local, uncommitted) ───────────────┐
 *   type ───▶│                                                           │
 *            └──┬──────────┬───────────────┬───────────────┬─────────────┘
 *               │ blur     │ Enter         │ Escape        │ idle Nms
 *               ▼          ▼               ▼               ▼
 *            COMMIT     COMMIT          REVERT to        COMMIT
 *                       (+ never        last committed   (autosave,
 *                        submits /       value            optional)
 *                        navigates)
 *
 *   Space is a CHARACTER, never a command. Only a real checkbox/switch
 *   toggles on it — and those are native `<input>`s, so ARIA gives that
 *   for free and this hook never touches them.
 *
 * SANCTIONED DEVIATIONS (deliberate, do not "fix"):
 *   · multiline textareas — Enter must insert a newline, so they submit on
 *     Ctrl/Cmd+Enter instead (see `NewComment.tsx`). Never wire this hook to
 *     a textarea whose Enter means "newline".
 *   · checkboxes / switches — Space toggles (ARIA). They are discrete, have
 *     no draft state, and commit on the toggle itself.
 *   · discrete pickers (select / autocomplete / date) — the selection IS the
 *     commit; Enter/Escape belong to the open popup's listbox.
 *
 * The grid half of the same law lives in `src/modules/grids/keyboard/` — same
 * sentence, different surface: EVERY way of leaving a field commits, keys differ
 * only in where focus goes, and discard is Escape alone.
 *
 * USAGE
 *   const po = useFieldCommit({ value: customerPo, onCommit: saveCustomerPo,
 *                               autosaveDelayMs: DEBOUNCE_DELAY_HIGH });
 *   <AgInput label="Customer PO#" {...po} />
 *
 * Under `EditingStateProvider` prefer `useEditStateFieldCommit` — same options,
 * plus the pending-draft registration that makes status transitions wait.
 */

export type FieldCommitHandlers<T> = {
  /** The value to render — the local draft, not the parent's saved value. */
  value: T;
  onChange: (next: T) => void;
  onBlur: () => void;
  onKeyDown: (event: React.KeyboardEvent<Element>) => void;
};

export type UseFieldCommitOptions<T> = {
  /** The last value the server/parent considers saved. Re-syncs the draft while the field is clean. */
  value: T;
  /** Called once per commit, only when the draft actually differs from the last committed value. */
  onCommit: (next: T) => void;
  /**
   * Streaming escape hatch for hosts whose parent already owns a per-keystroke
   * pipeline (its own state + debounce). Called on EVERY draft change and on
   * revert, so the parent's saved value can never end up holding an abandoned
   * edit. Leave undefined for the normal commit-only contract.
   */
  onDraftChange?: (next: T) => void;
  /** When set, an idle period of this many ms also commits (autosave). Omit for commit-on-leave only. */
  autosaveDelayMs?: number;
  /**
   * Fires when the field gains (`true`) / loses (`false`) an uncommitted edit.
   * `useEditStateFieldCommit` bridges it into `EditingStateProvider` so a status
   * transition waits for the draft instead of racing it.
   */
  onPendingChange?: (isPending: boolean) => void;
};

/**
 * Wires the One Law onto a single controlled field. Returns props to spread
 * straight onto `AgInput` (or any input exposing value/onChange/onBlur/onKeyDown).
 */
export function useFieldCommit<T>({
  value,
  onCommit,
  onDraftChange,
  autosaveDelayMs,
  onPendingChange,
}: UseFieldCommitOptions<T>): FieldCommitHandlers<T> {
  const [draft, setDraft] = useState<T>(value);

  // The value the field would revert to on Escape: what was last handed to
  // `onCommit` (or seeded from the parent while the field was clean).
  const committedRef = useRef<T>(value);
  const draftRef = useRef<T>(value);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refs so the unmount flush and the timer callback always see the latest
  // callbacks without re-arming the timer on every parent render.
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  const onPendingChangeRef = useRef(onPendingChange);
  onPendingChangeRef.current = onPendingChange;
  const isPendingRef = useRef(false);

  /** Announce the uncommitted-edit window exactly once per transition. */
  const setPending = useCallback((isPending: boolean) => {
    if (isPendingRef.current === isPending) {
      return;
    }

    isPendingRef.current = isPending;
    onPendingChangeRef.current?.(isPending);
  }, []);

  const setDraftValue = useCallback((next: T) => {
    draftRef.current = next;
    setDraft(next);
  }, []);

  const cancelPending = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /** The single commit path — blur, Enter, autosave and the unmount flush all land here. */
  const commit = useCallback(() => {
    cancelPending();

    const next = draftRef.current;
    if (Object.is(next, committedRef.current)) {
      setPending(false);
      return;
    }

    committedRef.current = next;
    setPending(false);
    onCommitRef.current(next);
  }, [cancelPending, setPending]);

  const handleChange = useCallback(
    (next: T) => {
      setDraftValue(next);
      setPending(!Object.is(next, committedRef.current));
      onDraftChange?.(next);

      if (autosaveDelayMs === undefined) {
        return;
      }

      cancelPending();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        commit();
      }, autosaveDelayMs);
    },
    [setDraftValue, setPending, onDraftChange, autosaveDelayMs, cancelPending, commit]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<Element>) => {
      // Mid-IME-composition Enter/Escape belong to the input method, not to us.
      if ((event.nativeEvent as KeyboardEvent | undefined)?.isComposing) {
        return;
      }

      if (event.key === 'Enter' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        // preventDefault stops implicit form submission; stopPropagation stops an
        // ancestor (dialog "save", router link, page-level shortcut) from also
        // acting on the same press. Enter means "commit THIS field" and nothing else.
        event.preventDefault();
        event.stopPropagation();
        commit();
        return;
      }

      if (event.key === 'Escape') {
        const isDirty = !Object.is(draftRef.current, committedRef.current);
        if (!isDirty) {
          // Nothing to abandon — let Escape bubble so dialogs/popovers still close.
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        cancelPending();
        setDraftValue(committedRef.current);
        setPending(false);
        onDraftChange?.(committedRef.current);
      }

      // Every other key — Space included — is a character. We do not touch it.
    },
    [commit, cancelPending, setDraftValue, setPending, onDraftChange]
  );

  // Re-sync from the parent only while the field is clean, so a background
  // refetch can never clobber what the user is typing.
  useEffect(() => {
    if (!Object.is(draftRef.current, committedRef.current)) {
      return;
    }

    committedRef.current = value;
    setDraftValue(value);
  }, [value, setDraftValue]);

  // Flush a pending edit on unmount. Parity with `useDebounce` (TAS-3436):
  // typing then navigating away inside the autosave window must not drop input.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      const pendingValue = draftRef.current;
      const hasPendingEdit = !Object.is(pendingValue, committedRef.current);

      if (isPendingRef.current) {
        isPendingRef.current = false;
        onPendingChangeRef.current?.(false);
      }

      if (hasPendingEdit) {
        committedRef.current = pendingValue;
        onCommitRef.current(pendingValue);
      }
    };
  }, []);

  return { value: draft, onChange: handleChange, onBlur: commit, onKeyDown: handleKeyDown };
}
